import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  MenuItem,
  Button,
  Card,
  CardContent,
  Divider,
  Alert,
  Snackbar,
  Fade,
  Chip,
  Stack,
  useTheme,
  Link,
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Feedback as FeedbackIcon,
  School as SchoolIcon,
  SupportAgent as SupportAgentIcon,
} from '@mui/icons-material';
import Copyright from '../../components/Copyright';
import { useNotification } from '../../context/NotificationContext';
import { useAccount } from '../../context/AccountContext';
import { SUPPORT_EMAIL } from '../../common/AppConstants';
import { httpRequest } from '../../api/helper';
import { API_ERROR_MESSAGE } from '../../common/AppConstants';

const DEMO_API_URL = 'user/demo-request/';

export default function Support() {
  // const theme = useTheme();
  const { addNotification } = useNotification();
  const { account } = useAccount();

  const [formData, setFormData] = useState({
    request_type: 'support',
    institute_name: account?.organization?.name || '',
    institute_type: '',
    contact_email: account?.user?.email || '',
    // contact_phone: account?.user?.phone || null,
    other_details: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      institute_name: account?.organization?.name || '',
      contact_email: account?.user?.email || '',
    }));
  }, [account]);

  const institutionTypes = [
    { value: 'school', label: 'School' },
    { value: 'college', label: 'College' },
    { value: 'university', label: 'University' },
    { value: 'training_center', label: 'Training Center' },
    { value: 'other', label: 'Other' }
  ];

  const emailContacts = [
    {
      title: 'Email',
      email: SUPPORT_EMAIL,
      description: 'For any queries or concerns, email us at',
      icon: <EmailIcon sx={{ color: 'primary.main', mr: 1 }} />
    },
    // {
    //   title: 'Technical Issues',
    //   email: SUPPORT_EMAIL,
    //   description: 'For technical support and troubleshooting',
    //   icon: <BusinessIcon />
    // },
    // {
    //   title: 'Business Inquiries',
    //   email: SUPPORT_EMAIL,
    //   description: 'For partnerships and business questions',
    //   icon: <SchoolIcon />
    // }
  ];

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.institute_name.trim()) {
      newErrors.institute_name = 'Institution name is required';
    }
    
    if (!formData.institute_type) {
      newErrors.institute_type = 'Institution type is required';
    }
    
    if (!formData.contact_email.trim()) {
      newErrors.contact_email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contact_email)) {
      newErrors.contact_email = 'Please enter a valid email address';
    }
    
    if (!formData.other_details.trim()) {
      newErrors.other_details = 'Query/Feedback is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
      
    await httpRequest({
      method: 'POST',
      url: DEMO_API_URL,
      data: formData,
      onSuccess: (data) => {
        setFormData(prev => ({
          ...prev,
          other_details: ''
        }));
        addNotification("Thank you for your submission! We'll get back to you soon.", 'info');
      },
      onFailure: (data) => {
        setErrors(data);
      },
      addNotification,
    });

    setIsSubmitting(false);
  };

  return (
    <Container
      component="main"
      maxWidth="lg"
    >
      {/* Header */}
      <Box
        sx={{
          textAlign: 'center',
          py: 6,
          mb: 4,
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        }} />
        <Typography 
          variant="h2" 
          component="h1" 
          gutterBottom
          color="primary"
          sx={{ 
            fontWeight: 'bold',
            position: 'relative',
            // zIndex: 1
          }}
        >
          Support Center
        </Typography>
        <Typography 
          variant="h5"
          color="primary"
          sx={{ 
            opacity: 0.9,
            position: 'relative',
            zIndex: 1
          }}
        >
          We're here to help you with any questions or concerns
        </Typography>
      </Box>

      <Grid container spacing={4}>
        {/* Feedback Form Section */}
        <Grid item xs={12} md={7}>
          <Paper 
            elevation={3}
            sx={{ 
              p: 4,
              borderRadius: 3,
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <FeedbackIcon sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
              <Typography color="primary" variant="h4" component="h2" sx={{ fontWeight: 'bold' }}>
                Submit Query/Feedback
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Institution Name"
                    value={formData.institute_name}
                    onChange={handleInputChange('institute_name')}
                    error={!!errors.institute_name}
                    helperText={errors.institute_name}
                    required
                    variant="outlined"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)'
                        }
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    select
                    label="Institution Type"
                    value={formData.institute_type}
                    onChange={handleInputChange('institute_type')}
                    error={!!errors.institute_type}
                    helperText={errors.institute_type}
                    required
                    variant="outlined"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)'
                        }
                      }
                    }}
                  >
                    {institutionTypes.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Email Address"
                    value={formData.contact_email}
                    onChange={handleInputChange('contact_email')}
                    error={!!errors.contact_email}
                    helperText={errors.contact_email}
                    required
                    variant="outlined"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)'
                        }
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="tel"
                    label="Phone Number"
                    value={formData.contact_phone}
                    onChange={handleInputChange('contact_phone')}
                    placeholder="+91 91234 56789" // "+1 (555) 123-4567"
                    variant="outlined"
                    InputProps={{
                      startAdornment: <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: "small" }} />
                    }}
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)'
                        }
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Query/Feedback"
                    value={formData.other_details}
                    onChange={handleInputChange('other_details')}
                    error={!!errors.other_details}
                    helperText={errors.other_details}
                    required
                    placeholder="Please describe your query or provide feedback in detail..."
                    variant="outlined"
                    sx={{ 
                      '& .MuiOutlinedInput-root': {
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-1px)'
                        }
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="small"
                    disabled={isSubmitting}
                    // startIcon={isSubmitting ? null : <SendIcon />}
                    sx={{
                      py: 1,
                      px: 3,
                      fontSize: '1.0rem',
                      fontWeight: 'bold',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                      },
                      '&:active': {
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Email Support Section */}
        <Grid item xs={12} md={5}>
          <Fade in timeout={1200}>
            <Paper 
              elevation={3}
              sx={{ 
                p: 4, 
                height: 'fit-content',
                borderRadius: 3,
                background: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 1,
                    border: '3px solid #233836',
                    borderColor: 'primary.main',
                    borderRadius: 2, // optional: for a circular border
                  }}
                >
                  <SupportAgentIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                </Box>
                <Typography variant="h4" color="primary" component="h2" sx={{ fontWeight: 'bold' }}>
                  Contact Us
                </Typography>
              </Box>
              
              <Stack spacing={2}>
                {emailContacts.map((contact, index) => (
                  <Card 
                    key={index}
                    sx={{ 
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: 3
                      }
                    }}
                  >
                    <CardContent>
                      <Typography variant="body2" color="primary">
                        {contact.description}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', my: 1 }}>
                        {contact.icon}
                        <Typography color="primary" variant="body1" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                          <Link href={`mailto:${contact.email}`} sx={{ fontWeight: 'bold' }}>
                            {contact.email}
                          </Link>
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Paper>
          </Fade>
        </Grid>
      </Grid>

      <Copyright sx={{ mt: 8, mb: 4 }} />
    </Container>
  );
};
